import {
  AdditiveBlending,
  BoxGeometry,
  BufferAttribute,
  type BufferGeometry,
  Color,
  ConeGeometry,
  CylinderGeometry,
  DoubleSide,
  Group,
  type Material,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  Quaternion,
  SphereGeometry,
  Vector3,
} from 'three';
import { EMIT, SURFACE } from '../render/palette';
import { applyContactShading, emitter, flatVertexColor, surface } from '../render/materials';
import { markRig } from './merge-static';
import { BALANCE } from '../sim/balance';

/**
 * THE GUARD, AS A THING YOU SEE.
 *
 * The mall is full of mannequins and the guards have to not be one. That is the
 * entire brief for this model. Everything else in the building is pale clay,
 * static, and roughly person-shaped, so a figure that is dark, banded in
 * retroreflective yellow, carrying a moving light and swinging its arms reads as
 * ALIVE from across the atrium at a glance — which is exactly how long you get.
 *
 * The torch is the important half. You will see the beam sweep round a corner
 * three seconds before you see the person holding it, and those three seconds
 * are the warning the stealth loop is built on. It is drawn as an additive cone,
 * not as a spotlight: three.js forward-renders, every real light is paid for by
 * every lit fragment on screen, and the measured budget is four. A cone of
 * additive geometry costs one draw call and gives the volumetric shaft the
 * reference sheet is full of. The actual illumination comes from a point light
 * registered with the LightDirector, which decides whether this guard's torch is
 * one of the four worth rendering from where the camera is standing.
 *
 * The walk cycle is four rotations driven by ground speed, with no skeleton and
 * no clips. Legs counter-swing, arms counter-swing against the legs, the hips
 * bob at twice the stride frequency and the torso leans into the run. It is the
 * cheapest possible animation and at the distances this game is played at the
 * difference between it and a rigged character is nothing.
 */

const M = BALANCE.move;
const G = BALANCE.guard;

/** Stride cycles per metre travelled. Tuned so the feet do not skate. */
const STRIDE = 0.62;

/**
 * Shoulder pitch of the torch arm, in radians. Held forward and slightly down,
 * the way anyone actually carries a torch they are using, and — more usefully —
 * held nearly still, so the beam is a steady line you can read from across the
 * atrium instead of a strobe.
 */
const TORCH_ARM = -1.15;

export interface PoseInput {
  /** Ground speed in metres per second. Drives the whole cycle. */
  speed: number;
  dt: number;
  /** Head yaw relative to the body, radians. Clamped to a neck's worth. */
  headYaw: number;
  headPitch: number;
  /** 0 standing, 1 flat on the floor. */
  down: number;
}

export class GuardFigure {
  readonly root = new Group();

  private readonly hips = new Group();
  private readonly torso = new Group();
  private readonly neck = new Group();
  private readonly legL = new Group();
  private readonly legR = new Group();
  private readonly armL = new Group();
  private readonly armR = new Group();
  private readonly lamp = new Object3D();
  private readonly beam: Mesh;
  private readonly flare: Mesh;
  private phase = 0;
  private lean = 0;

  constructor(readonly name: string) {
    this.root.name = `guard:${name}`;
    // Named, so `mergeStatic` leaves the whole subtree alone. A guard folded
    // into a static batch is a guard welded to the floor. The name is not
    // enough on its own — every OTHER named group gets its anonymous interior
    // merged, and a guard's forearm is anonymous and still moves. See `markRig`.
    markRig(this.root);
    this.root.add(this.hips);
    this.hips.add(this.torso, this.legL, this.legR);
    this.torso.add(this.neck, this.armL, this.armR);

    const cloth = surface('uniform', SURFACE.uniform, { roughness: 0.92 });
    const clothDark = surface('uniformDark', SURFACE.uniformDark, { roughness: 0.94 });
    const trim = surface('uniformTrim', SURFACE.uniformTrim, { roughness: 0.8, metalness: 0.1 });
    const hiVis = emitter('hiVis', EMIT.hiVis, 0.5);
    const skin = surface('mannequin', SURFACE.mannequin, { roughness: 0.78 });

    // --- legs --------------------------------------------------------------
    // Pivoted at the hip, so rotating the group swings the whole leg.
    for (const [group, side] of [
      [this.legL, -1],
      [this.legR, 1],
    ] as const) {
      group.position.set(side * 0.13, 0.88, 0);
      const thigh = mesh(new CylinderGeometry(0.095, 0.082, 0.48, 7), clothDark, 0.5);
      thigh.position.y = -0.24;
      const shin = mesh(new CylinderGeometry(0.082, 0.07, 0.44, 7), clothDark, 0.4);
      shin.position.y = -0.68;
      const boot = mesh(new BoxGeometry(0.13, 0.1, 0.26), trim, 0);
      boot.position.set(0, -0.87, 0.03);
      group.add(thigh, shin, boot);
    }

    // --- torso -------------------------------------------------------------
    this.torso.position.y = 0.88;
    const chest = mesh(new BoxGeometry(0.42, 0.5, 0.24), cloth, 0.5);
    chest.position.y = 0.25;
    const waist = mesh(new BoxGeometry(0.36, 0.16, 0.22), clothDark, 0.1);
    waist.position.y = -0.04;
    this.torso.add(chest, waist);

    // The vest. Two horizontal bands and two braces over the shoulders — the
    // standard pattern, and the only part of a guard visible at range.
    for (const y of [0.16, 0.34]) {
      const band = new Mesh(flatVertexColor(new BoxGeometry(0.44, 0.055, 0.26)), hiVis);
      band.position.y = y;
      this.torso.add(band);
    }
    for (const side of [-1, 1]) {
      const brace = new Mesh(flatVertexColor(new BoxGeometry(0.055, 0.3, 0.26)), hiVis);
      brace.position.set(side * 0.13, 0.4, 0);
      this.torso.add(brace);
    }
    // Duty belt, because a silhouette with a straight waist reads as furniture.
    const belt = mesh(new BoxGeometry(0.4, 0.07, 0.24), trim, 0);
    belt.position.y = -0.1;
    this.torso.add(belt);

    // --- head --------------------------------------------------------------
    this.neck.position.y = 0.56;
    const head = mesh(new SphereGeometry(0.115, 8, 6), skin, 0);
    head.scale.set(1, 1.16, 0.94);
    head.position.y = 0.12;
    const capCrown = mesh(new CylinderGeometry(0.125, 0.128, 0.09, 8), clothDark, 0);
    capCrown.position.y = 0.2;
    const peak = mesh(new BoxGeometry(0.2, 0.022, 0.14), clothDark, 0);
    peak.position.set(0, 0.17, -0.13);
    const capBand = new Mesh(flatVertexColor(new BoxGeometry(0.26, 0.028, 0.26)), hiVis);
    capBand.position.y = 0.155;
    capBand.scale.set(1, 1, 1);
    this.neck.add(head, capCrown, peak, capBand);

    // --- arms --------------------------------------------------------------
    for (const [group, side] of [
      [this.armL, -1],
      [this.armR, 1],
    ] as const) {
      group.position.set(side * 0.25, 0.4, 0);
      const upper = mesh(new CylinderGeometry(0.058, 0.05, 0.34, 6), cloth, 0.3);
      upper.position.y = -0.17;
      const fore = mesh(new CylinderGeometry(0.05, 0.045, 0.32, 6), clothDark, 0.2);
      fore.position.y = -0.49;
      const cuff = new Mesh(flatVertexColor(new BoxGeometry(0.11, 0.04, 0.11)), hiVis);
      cuff.position.y = -0.34;
      group.add(upper, fore, cuff);
    }
    // Shoulder radio. Pure silhouette detail; it costs six triangles and it is
    // the thing that says "this person is talking to someone".
    const radio = mesh(new BoxGeometry(0.06, 0.1, 0.045), trim, 0);
    radio.position.set(-0.18, 0.42, -0.1);
    this.torso.add(radio);

    // --- torch -------------------------------------------------------------
    // Carried in the right hand, so the whole beam swings with the arm.
    this.lamp.position.set(0, -0.68, -0.1);
    this.armR.add(this.lamp);
    const barrel = mesh(new CylinderGeometry(0.032, 0.026, 0.2, 8), trim, 0);
    barrel.rotation.x = Math.PI / 2;
    barrel.position.z = -0.06;
    this.lamp.add(barrel);

    const lens = new Mesh(flatVertexColor(new SphereGeometry(0.05, 8, 6)), emitter('torchLens', EMIT.torch, 2.4));
    lens.position.z = -0.15;
    lens.scale.set(1, 1, 0.5);
    this.lamp.add(lens);

    this.beam = torchBeam(G.torchRangeM, G.torchDeg);
    this.beam.position.z = -0.14;
    this.lamp.add(this.beam);

    // A soft blob at the lamp itself. Without it the beam appears to come from
    // nowhere when you are looking down its length.
    this.flare = new Mesh(
      flatVertexColor(new SphereGeometry(0.26, 10, 8)),
      new MeshBasicMaterial({
        color: EMIT.torch.clone().multiplyScalar(0.5),
        transparent: true,
        blending: AdditiveBlending,
        depthWrite: false,
        toneMapped: true,
        fog: true,
      }),
    );
    this.flare.position.z = -0.16;
    this.flare.renderOrder = 3;
    this.lamp.add(this.flare);
  }

  /** World position of the torch lamp. The point light follows this. */
  lampWorld(out = new Vector3()): Vector3 {
    return this.lamp.getWorldPosition(out);
  }

  /** Unit vector the torch points along, in world space. */
  lampDir(out = new Vector3()): Vector3 {
    return out.set(0, 0, -1).applyQuaternion(this.lamp.getWorldQuaternion(SCRATCH_Q)).normalize();
  }

  torchOn(on: boolean): void {
    this.beam.visible = on;
    this.flare.visible = on;
  }

  pose(p: PoseInput): void {
    // Stride frequency follows distance covered, not time, so a guard slowing to
    // a stop does not moonwalk the last half-second.
    this.phase += p.speed * p.dt * STRIDE * Math.PI * 2;
    const swing = Math.min(1, p.speed / M.runSpeed) * 0.85;
    const s = Math.sin(this.phase);
    const c = Math.cos(this.phase);

    this.legL.rotation.x = s * swing;
    this.legR.rotation.x = -s * swing;
    // The left arm swings. The right one is holding the torch out in front and
    // barely moves — which is both what people actually do and, more to the
    // point, what stops the beam from scything up and down the walls once per
    // stride. A torch that swings with the arm is unreadable as a warning.
    this.armL.rotation.x = s * swing * 0.75;
    this.armL.rotation.z = 0.08 + swing * 0.05;
    this.armR.rotation.x = TORCH_ARM + s * swing * 0.06;
    this.armR.rotation.z = -0.14;

    // Hips rise twice per stride and roll onto the loaded leg.
    this.hips.position.y = Math.abs(c) * 0.035 * swing;
    this.hips.rotation.z = s * 0.05 * swing;

    // Lean into the run, eased so a sudden stop does not snap upright.
    const wantLean = Math.min(1, p.speed / M.runSpeed) * 0.16;
    this.lean += (wantLean - this.lean) * Math.min(1, p.dt * 4);
    this.torso.rotation.x = this.lean;

    const yaw = clamp(p.headYaw, -1.1, 1.1);
    const pitch = clamp(p.headPitch, -0.6, 0.6);
    this.neck.rotation.y = yaw;
    this.neck.rotation.x = pitch - this.lean;

    // Cancel the arm's rotation out of the lamp and re-aim it along the line of
    // sight, so the beam always points where the guard is looking no matter what
    // the shoulder is doing. Slightly under-following the head: a torch tracks a
    // turn a fraction behind the eyes, and that lag is what makes the sweep read
    // as somebody searching rather than a cone bolted to a face.
    this.lamp.rotation.x = -this.armR.rotation.x - this.lean + pitch * 0.7;
    this.lamp.rotation.y = yaw * 0.8;

    // Downed: fold at the hips and drop. Not a ragdoll, but at a glance it is
    // unmistakably a person on the floor rather than a person standing still.
    if (p.down > 0) {
      this.root.rotation.x = -p.down * Math.PI * 0.46;
      this.root.position.y = -p.down * 0.1;
      this.legL.rotation.x = p.down * 0.6;
      this.legR.rotation.x = p.down * 0.35;
      this.armL.rotation.x = p.down * 1.1;
      this.armR.rotation.x = p.down * 0.9;
    } else if (this.root.rotation.x !== 0) {
      this.root.rotation.x = 0;
      this.root.position.y = 0;
    }
  }
}

const SCRATCH_Q = new Quaternion();

/**
 * The visible shaft of a torch, as one additive cone.
 *
 * Apex at the origin pointing along -Z, matching the engine's forward. Brightness
 * is carried in the vertex colours and falls to nothing at the far end, so the
 * beam fades out rather than terminating in a disc of light hanging in mid-air.
 * Double-sided on purpose: a beam pointed at the camera has to be brighter than
 * one seen side-on, and drawing both faces gets that for free.
 *
 * WHY THE RIM FADE IS NOT OPTIONAL. A cone shell is a lie about a volume, and the
 * lie shows at the silhouette. Fading along the length alone leaves every
 * cross-section uniformly bright out to the rim, so the edge of the cone is a
 * hard polygon line — seen side-on from across the atrium the beam stops being
 * light and becomes a grey wedge laid over the shop fronts, with a straight seam
 * dragging across the floor as the guard walks. It reads as a rendering fault,
 * because it is one.
 *
 * What a real shaft does is get brighter the more fog the eye is looking through,
 * and the chord an eye ray cuts through a cone goes to zero exactly at the
 * silhouette. On a shell that chord is proportional to how face-on the surface
 * is, which is `|dot(N, V)|` — so that term IS the volume integral, cheaply. It
 * costs two varyings and no extra draw, and it turns the wedge back into a beam.
 */
export function torchBeam(range: number, fullAngleDeg: number): Mesh {
  const radius = Math.tan((fullAngleDeg * Math.PI) / 360) * range;
  // 14 segments left a visibly faceted silhouette once the rim fade made the
  // silhouette the part you look at. 28 is still one draw call.
  const geo = new ConeGeometry(radius, range, 28, 6, true);
  geo.translate(0, -range / 2, 0);
  geo.rotateX(Math.PI / 2);

  // Fade along the beam. The exponent matters more than the endpoints: linear
  // reads as a solid ice-cream cone, and squared reads as light. The short ramp
  // at the near end stops the cone beginning as a bright disc floating a hand's
  // width in front of the lamp.
  const pos = geo.getAttribute('position');
  const col = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i++) {
    const t = Math.min(1, -pos.getZ(i) / range);
    const v = Math.pow(1 - t, 2.1) * Math.min(1, t / 0.06) * 0.9;
    col[i * 3] = v;
    col[i * 3 + 1] = v;
    col[i * 3 + 2] = v;
  }
  geo.setAttribute('color', new BufferAttribute(col, 3));

  const material = new MeshBasicMaterial({
    color: new Color(EMIT.torch).multiplyScalar(0.16),
    vertexColors: true,
    transparent: true,
    blending: AdditiveBlending,
    depthWrite: false,
    side: DoubleSide,
    toneMapped: true,
    fog: true,
  });
  material.onBeforeCompile = (shader) => {
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vBeamN;\nvarying vec3 vBeamV;')
      .replace(
        '#include <project_vertex>',
        '#include <project_vertex>\nvBeamN = normalMatrix * normal;\nvBeamV = -mvPosition.xyz;',
      );
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vBeamN;\nvarying vec3 vBeamV;')
      .replace(
        '#include <color_fragment>',
        `#include <color_fragment>
        // Chord through the cone, approximated by how face-on the shell is.
        float beamRim = abs(dot(normalize(vBeamN), normalize(vBeamV)));
        // Below one it would fatten the edge back up; above one it pinches the
        // beam into a thread. Just over one keeps the core and kills the seam.
        diffuseColor.rgb *= pow(beamRim, 1.15);`,
      );
  };
  // Mandatory alongside onBeforeCompile: without it three.js reuses whichever
  // basic-material program it compiled first and the injection silently vanishes.
  material.customProgramCacheKey = () => 'torchBeam';

  const mesh = new Mesh(geo, material);
  mesh.name = 'torchBeam';
  mesh.renderOrder = 3;
  // Never culled: the bounding sphere of a 16m cone attached to a swinging arm
  // is wrong often enough to make the beam blink out at the worst moment.
  mesh.frustumCulled = false;
  return mesh;
}

function mesh(geo: BufferGeometry, mat: Material, contact: number): Mesh {
  if (contact > 0) applyContactShading(geo, { falloff: contact, strength: 0.32 });
  else flatVertexColor(geo);
  const m = new Mesh(geo, mat);
  m.castShadow = true;
  return m;
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
